import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useSelector } from "react-redux";
import { v4 as uuid } from "uuid";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import CloseIcon from "@material-ui/icons/Close";
import A_SimpleDialog from "../../../Atoms/Feedback/Dialogs/A_SimpleDialog";
import A_Button from "../../../Atoms/Inputs/Buttons/A_Button";
import IconAction from "./IconAction";
import {
  FIRM_DOCUMENT_ALLOWED_EXTENSIONS,
  FIRM_DOCUMENT_MAX_SIZE_BYTES,
  isAllowedFirmDocumentExtension,
  isFirmDocumentTooLarge,
  isValidFirmDocumentFileName,
  buildFirmDocumentFilePath,
} from "../../../../../domain/professionalContacts/professionalContacts.rules";
import { getFileBadgeClass } from "../DocumentsDashboard/DocumentsDashboard.helper";
import "../DocumentsDashboard/DocumentsDashboard.css";
import { api } from "./ProfessionalContacts.helper";
import useStyles from "./ProfessionalContacts.styles";

// All user-facing copy for this dialog lives here (LLD §3.6) — the feature has no i18n layer.
const CHOOSE_FILE_ERROR = "Choose at least one file to upload.";
const FILE_NAME_ERROR =
  "File name can't contain a slash and must be 255 characters or fewer.";
const UNSUPPORTED_TYPE_ERROR = `Unsupported file type. Allowed: ${FIRM_DOCUMENT_ALLOWED_EXTENSIONS.join(", ")}`;
const MAX_SIZE_MB = Math.round(FIRM_DOCUMENT_MAX_SIZE_BYTES / (1024 * 1024));
const TOO_LARGE_ERROR = `File is too large. Maximum size is ${MAX_SIZE_MB} MB.`;
const GENERIC_ERROR = "Upload failed. Please try again.";
const HINT_TEXT = `PDF, Word, Excel or image · max ${MAX_SIZE_MB} MB per file`;

const extensionOf = (fileName) =>
  fileName.slice(fileName.lastIndexOf(".")).toLowerCase();

// Badge key has no leading dot (matches FILE_TYPE_BADGE_STYLES' keys, e.g. "pdf" not ".pdf") —
// reuses the same fileTypeBadge styling as the Documents table/section rows, for a consistent
// pdf/doc/xls/etc. look across the whole feature.
const badgeExtensionOf = (fileName) => extensionOf(fileName).replace(".", "");

const formatFieldErrors = (fieldErrors) =>
  fieldErrors.map((fe) => `${fe.field}: ${fe.message}`).join("; ");

// Dedupe key for a picked File — mirrors M_AddTaskForm.jsx's pushFilesWithValidation, so re-picking
// (or drag-batching) a file already in the pending list is a silent no-op, not a duplicate row.
const fileKey = (file) => `${file.name}-${file.size}-${file.lastModified || 0}`;

/** name/extension/size, in that order (R7) — returns an error message, or null if the file is valid. */
const validateOneFile = (file) => {
  if (!isValidFirmDocumentFileName(file.name)) return FILE_NAME_ERROR;
  if (!isAllowedFirmDocumentExtension(file.name)) return UNSUPPORTED_TYPE_ERROR;
  if (isFirmDocumentTooLarge(file)) return TOO_LARGE_ERROR;
  return null;
};

/**
 * Add-document modal (LLD §3.4, HLD §4a.4): picks a file, validates it client-side before any
 * network call (R7), uploads the bytes then registers the metadata, and cleans up the blob if
 * the metadata registration fails (R9/HLD §5 F2).
 */
const FirmDocumentUploadDialog = ({ firmId, open, onClose, onUploaded }) => {
  const classes = useStyles();
  const oidcUser = useSelector((state) => state.oidc && state.oidc.user);
  const fileInputRef = useRef(null);

  const [files, setFiles] = useState([]);
  const [fileError, setFileError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // Re-arms the dialog every time it transitions closed -> open, so a previous attempt's files
  // or error never leaks into the next one — mirrors AddDocumentsDialog's own open-edge reset.
  const wasOpenRef = useRef(false);
  const reset = () => {
    setFiles([]);
    setFileError(null);
    setSubmitting(false);
    setSubmitError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  if (open && !wasOpenRef.current) {
    wasOpenRef.current = true;
    reset();
  } else if (!open) {
    wasOpenRef.current = false;
  }

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  // Validated per file, on pick (R7) — not only at submit. A file already in the pending list
  // (same name/size/lastModified) is silently skipped rather than duplicated. Shared by both the
  // hidden <input onChange> path and drag-and-drop, so a dropped file behaves identically to a
  // picked one.
  const addPickedFiles = (picked) => {
    const seenKeys = new Set(files.map(fileKey));
    const validFiles = [];
    const invalidMessages = [];
    picked.forEach((pickedFile) => {
      const key = fileKey(pickedFile);
      if (seenKeys.has(key)) return;
      const error = validateOneFile(pickedFile);
      if (error) {
        invalidMessages.push(`${pickedFile.name}: ${error}`);
        return;
      }
      seenKeys.add(key);
      validFiles.push(pickedFile);
    });

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }
    setFileError(invalidMessages.length > 0 ? invalidMessages.join("; ") : null);
  };

  const handleFileSelected = (event) => {
    const picked = Array.from(event.target.files || []);
    event.target.value = ""; // allow re-picking the same file(s) after a failure
    if (picked.length === 0) return;
    addPickedFiles(picked);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!submitting) setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    if (submitting) return;
    const dropped = Array.from(event.dataTransfer?.files || []);
    if (dropped.length > 0) addPickedFiles(dropped);
  };

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /**
   * The single-file upload-then-create sequence, unchanged in substance from the pre-multi-file
   * version — just extracted so it can run once per file. Returns the saved FirmDocumentDto or
   * throws an Error whose message names the file, so a partial-failure summary can tell files apart.
   */
  const uploadOneDocument = async (uploadFile) => {
    const documentId = uuid();
    const filePath = buildFirmDocumentFilePath(firmId, documentId, uploadFile.name);
    let uploadedOk = false;
    try {
      await api.uploadFirmDocumentBlob(firmId, documentId, uploadFile, oidcUser); // 1. bytes
      uploadedOk = true;
      return await api.createFirmDocument(firmId, {
        // 2. metadata
        documentId,
        fileName: uploadFile.name,
        filePath,
        fileExtension: extensionOf(uploadFile.name),
        contentType: uploadFile.type || null,
        sizeBytes: uploadFile.size,
      });
    } catch (err) {
      // Compensating cleanup for HLD §5 F2 — only if the bytes actually landed, and best-effort:
      // a storage failure here must never mask the original error the user needs to see.
      if (uploadedOk) {
        api.deleteFirmDocumentBlob(filePath, oidcUser).catch(() => {});
      }
      const message =
        err.fieldErrors && err.fieldErrors.length
          ? formatFieldErrors(err.fieldErrors)
          : err.message || GENERIC_ERROR;
      throw new Error(`${uploadFile.name}: ${message}`);
    }
  };

  const handleSubmit = async () => {
    if (files.length === 0) {
      // Don't stomp a still-visible pick-time rejection (e.g. "bad.exe: Unsupported file type")
      // with the generic prompt — only show it when nothing was ever picked, valid or not.
      if (!fileError) setFileError(CHOOSE_FILE_ERROR);
      return;
    }
    setSubmitError(null);
    setSubmitting(true);

    // Every file gets its own documentId/blob/metadata pair, fired in parallel — mirrors
    // AddDocumentsDialog's runDirectUpload fan-out shape (Promise.allSettled + tally), adapted from
    // its single-call-per-file shape to this dialog's two-call-per-file (blob + create) one.
    const results = await Promise.allSettled(files.map(uploadOneDocument));

    const succeededDocs = [];
    const remainingFiles = [];
    const failedMessages = [];
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        succeededDocs.push(result.value);
      } else {
        remainingFiles.push(files[index]);
        failedMessages.push(result.reason.message || GENERIC_ERROR);
      }
    });

    setSubmitting(false);
    // Successes are never rolled back or re-attempted (HLD §5) — only the failed files stay in the
    // pending list, so the user can retry or remove them without re-picking everything.
    setFiles(remainingFiles);
    if (succeededDocs.length > 0) onUploaded(succeededDocs);

    if (failedMessages.length === 0) {
      onClose();
    } else {
      setSubmitError(failedMessages.join("; "));
    }
  };

  const content = (
    <div>
      {submitError && (
        <div className={classes.validationSummary}>
          <ErrorOutlineIcon className={classes.validationIcon} />
          <span>{submitError}</span>
        </div>
      )}

      <Box className={classes.uploadSectionCard}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={FIRM_DOCUMENT_ALLOWED_EXTENSIONS.join(",")}
          hidden
          onChange={handleFileSelected}
        />
        <Box
          className={clsx(classes.docDropzone, isDragOver && classes.docDropzoneActive)}
          onClick={submitting ? undefined : handleChooseFile}
          onDragOver={handleDragOver}
          onDragEnter={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          role="button"
          aria-label="Choose files"
          tabIndex={submitting ? -1 : 0}
          onKeyDown={(event) => {
            if (submitting) return;
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              handleChooseFile();
            }
          }}
        >
          <CloudUploadOutlinedIcon className={classes.docDropzoneIcon} />
          <Typography className={classes.docDropzoneText}>
            Drop files or <span className={classes.docDropzoneBrowse}>browse</span>
          </Typography>
          <Typography className={classes.uploadHint}>{HINT_TEXT}</Typography>
        </Box>
        {files.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 6,
              marginTop: 8,
            }}
          >
            {files.map((pendingFile, index) => (
              <div
                key={fileKey(pendingFile)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  paddingBottom: 6,
                  borderBottom:
                    index < files.length - 1
                      ? "1px solid var(--total_row_boldbackground)"
                      : "none",
                }}
              >
                <span
                  className={clsx(
                    "fileTypeBadge",
                    classes.documentIcon,
                    getFileBadgeClass(badgeExtensionOf(pendingFile.name))
                  )}
                  style={{ flexShrink: 0 }}
                >
                  {badgeExtensionOf(pendingFile.name) || "file"}
                </span>
                <span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>
                  {pendingFile.name}
                </span>
                <IconAction
                  title={`Remove ${pendingFile.name}`}
                  icon={<CloseIcon fontSize="small" />}
                  disabled={submitting}
                  onClick={() => handleRemoveFile(index)}
                />
              </div>
            ))}
          </div>
        )}
        {fileError && (
          <div
            style={{
              color: "var(--status-error-color)",
              fontSize: "0.75rem",
              marginTop: 4,
            }}
          >
            {fileError}
          </div>
        )}
      </Box>
    </div>
  );

  const actions = (
    <div style={{ display: "flex", gap: 8 }}>
      <A_Button
        label="Cancel"
        size="small"
        premiumVariant="secondary"
        onClick={handleClose}
        disabled={submitting}
      />
      <A_Button
        label={submitting ? "Uploading…" : "Upload"}
        size="small"
        premiumVariant="primary"
        onClick={handleSubmit}
        disabled={submitting}
      />
    </div>
  );

  return (
    <A_SimpleDialog
      open={open}
      onClose={handleClose}
      title="Add documents"
      maxWidth="sm"
      dialogContent={content}
      dialogActions={actions}
    />
  );
};

FirmDocumentUploadDialog.propTypes = {
  firmId: PropTypes.string.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUploaded: PropTypes.func.isRequired,
};

export default FirmDocumentUploadDialog;
